import axios from "axios"
import { ToolkitStore } from "@reduxjs/toolkit/dist/configureStore"
import * as Sentry from "@sentry/react"
import {
  updateToken,
  incrementRefreshTokenFailed,
} from "features/auth/authSlice"
import { resetStoreAction } from "config"
import {
  notifyIfAppOutDate,
  removeAxiosToken,
  setAxiosToken,
} from "./requester"
import dayjs from "dayjs"
import { isAuthError } from "utils"
import {
  showSnackbar,
  hideSnackbar,
  removeSnackbar,
} from "features/notification/notificationSlice"
import { v4 as uuidv4 } from "uuid"
import { getErrorMessage } from "utils"
import { getNowUtcIso } from "utils/dateTime"
import { removeZOZOFitAxiosToken } from "./zozoFitRequester"

export const refreshInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Timezone-Offset": dayjs().format("Z").split(":")[0],
  },
})

export const exchangeRefreshToken = (refreshToken: string) =>
  refreshInstance.put("/accounts/token", { refreshToken })

const refreshTokenFn = async (store: ToolkitStore) => {
  const { refreshToken } = store.getState().auth
  const usedRefreshToken = refreshToken // snapshot before async call

  try {
    const response = await exchangeRefreshToken(usedRefreshToken)
    const accessToken = response.data.accessToken
    store.dispatch(updateToken(accessToken))
    setAxiosToken(accessToken)
    return { accessToken }
  } catch (error) {
    const isAppOutDateError = notifyIfAppOutDate(error, store)
    if (isAppOutDateError) return

    if (isAuthError(error)) {
      const authState = store.getState().auth
      const hasRefreshFailedBefore = authState.refreshTokenFailedCount >= 1
      const tokenMismatch = usedRefreshToken !== authState.refreshToken
      const daysSinceFirstLogin = authState.firstLoginTime
        ? dayjs().diff(dayjs(authState.firstLoginTime), "day")
        : null
      const tooSoonExpiry =
        daysSinceFirstLogin !== null && daysSinceFirstLogin < 20

      if (hasRefreshFailedBefore || tokenMismatch || tooSoonExpiry) {
        Sentry.withScope((scope) => {
          scope.setFingerprint(["RefreshToken", "monitored-failure"])
          scope.setContext("refresh-state", {
            timestamp: getNowUtcIso(),
            firstLoginTime: authState.firstLoginTime,
            lastLoginTime: authState.lastLoginTime,
            firstLogoutTime: authState.firstLogoutTime,
            lastLogoutTime: authState.lastLogoutTime,
            refreshTokenFailedCount: authState.refreshTokenFailedCount,
            daysSinceFirstLogin,
            hasRefreshFailedBefore,
            tokenMismatch,
            tooSoonExpiry,
          })
          const cases = [
            hasRefreshFailedBefore && "previous refresh failure",
            tokenMismatch && "Case2: token mismatch",
            tooSoonExpiry &&
              `Case3: early expiry (${daysSinceFirstLogin}d since first login)`,
          ]
            .filter(Boolean)
            .join(" + ")
          Sentry.captureMessage(`[RefreshToken/failure] ${cases}`, "warning")
        })
      } else {
        Sentry.withScope((scope) => {
          scope.setFingerprint(["RefreshToken", "unmonitored-failure"])
          scope.setContext("refresh-state", {
            timestamp: getNowUtcIso(),
            firstLoginTime: authState.firstLoginTime,
            lastLoginTime: authState.lastLoginTime,
            refreshTokenFailedCount: authState.refreshTokenFailedCount,
            daysSinceFirstLogin,
          })
          Sentry.captureMessage(
            "[RefreshToken/failure] unmonitored case",
            "warning",
          )
        })
      }
      await Sentry.flush(2000)

      store.dispatch(incrementRefreshTokenFailed())
      store.dispatch(resetStoreAction())
      removeAxiosToken()
      removeZOZOFitAxiosToken()
    } else {
      const id = uuidv4()
      store.dispatch(
        showSnackbar({
          id,
          status: "active",
          type: "error",
          title: getErrorMessage(error),
        }),
      )
      setTimeout(() => {
        store.dispatch(hideSnackbar(id))
        setTimeout(() => store.dispatch(removeSnackbar(id)), 2000)
      }, 5000)
    }
  }
}

const maxAge = 20000

let cache: {
  promise: ReturnType<typeof refreshTokenFn>
  expiresAt: number
} | null = null

const memoizedRefreshToken = (store: ToolkitStore) => {
  if (cache && Date.now() < cache.expiresAt) {
    return cache.promise
  }

  const promise = refreshTokenFn(store)
  cache = { promise, expiresAt: Date.now() + maxAge }
  return promise
}

export default memoizedRefreshToken
